import { DeleteOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Popconfirm, Row, Select, Space, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLoginModal } from '../../../../context/loginModalContext'
import { useUser } from '../../../../context/userContext'
import { toEnglishNumbers } from '../../../../shared/utils/helpers'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../common/CommonStyles'
import AmountPriceField from '../../../common/formFields/AmountPriceField'
import EmailField from '../../../common/formFields/EmailField'
import LabelWithHelp from '../../../common/formFields/LabelWithHelp'
import PhoneField from '../../../common/formFields/PhoneField'
import SearchField from '../../../common/searchFields/SearchField'
import { SEARCH_PRICE_LISTS_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { useForceUpdate } from '../../../../shared/hooks/useForceUpdate'
import { v4 as uuidv4 } from 'uuid'
import CreateNewItemExtraButtons from '../../../common/CreateNewItemExtraButtons'
import ADD_NEW_INSURANCE_COMPANY from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/insuranceCompanies/addNewInsuranceCompany'
import EDIT_INSURANCE_COMPANY from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/insuranceCompanies/editInsuranceCompany'
import DELETE_INSURANCE_COMPANY from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/insuranceCompanies/deleteInsuranceCompany'
import moment from 'moment'
import SEARCH_INSURANCE_COMPANIES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/insuranceCompanies/searchInsuranceCompanies'
import { useFeatureFlag } from '@dentolize/clinic-web-canary'

const InsuranceCompanyForm = props => {
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { user } = useUser()
  const hasDhsFeature = useFeatureFlag('FEATURE_DHS_INTEGRATION')
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, getFieldValue, setFieldsValue } = form

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  const insuranceUnlimited = Form.useWatch('insuranceUnlimited', form)
  const fullDiscount = Form.useWatch('fullDiscount', form)
  const forcePolicySelect = Form.useWatch('forcePolicySelect', form)

  useEffect(() => {
    resetFields()
  }, [formVisible])

  useEffect(() => {
    if (insuranceUnlimited) {
      setFieldsValue({ insuranceLimit: null })
    }
  }, [insuranceUnlimited])

  useEffect(() => {
    if (fullDiscount) {
      setFieldsValue({ insurancePercentage: 100 })
    }
  }, [fullDiscount])

  const [addNewInsuranceCompany, { loading }] = useMutation(ADD_NEW_INSURANCE_COMPANY, {
    refetchQueries: [
      { query: SEARCH_INSURANCE_COMPANIES, variables: { searchTerm: '' } },
      'GET_INSURANCE_COMPANIES' // Refetch the listing query to show updated data
    ]
  })
  const [editInsuranceCompany, { loading: loadingEdit }] = useMutation(EDIT_INSURANCE_COMPANY, {
    refetchQueries: [
      'GET_INSURANCE_COMPANIES' // Refetch the listing query to show updated data
    ]
  })
  const [deleteInsuranceCompany, { loading: loadingDelete }] = useMutation(DELETE_INSURANCE_COMPANY, {
    update: handleUpdateAfterDelete
  })

  function handleUpdateAfterDelete(store, deletedItem) {
    store.data.delete(`InsuranceCompany:${deletedItem.data.deleteInsuranceCompany.id}`)
    props.handleAnimateRow && props.handleAnimateRow(null, true)
  }

  const handleSubmitCompany = key => {
    validateFields()
      .then(async values => {
        try {
          let insuranceCompany

          // With Force Policy Selection the limit / percentage fields are hidden (unmounted), so leave them
          // undefined and the server keeps the stored values instead of overwriting them with 0 / 100%
          const variables = {
            ...values,
            insurancePercentage: values.forcePolicySelect
              ? undefined
              : values.insurancePercentage || values.insurancePercentage === 0
              ? values.insurancePercentage
              : 100,
            insuranceStartMonth:
              values.insuranceStartMonth || values.insuranceStartMonth === 0 ? values.insuranceStartMonth : null,
            // "No limit" is stored as null, the same as on policy classes
            insuranceLimit: values.forcePolicySelect
              ? undefined
              : values.insuranceUnlimited
              ? null
              : values.insuranceLimit || 0,
            taxPercent: values.taxPercent,
            number: values.number ? `${values.suffix || ''}_${values.number}` : '',
            priceList: values.priceList?.key,
            nphiesCode: values.nphiesCode,
            nphiesInsuranceCompanyId: values.nphiesInsuranceCompanyId,
            tpaNphiesCode: values.tpaNphiesCode,
            // Address fields for ZATCA B2B invoices
            taxNumber: values.taxNumber,
            street: values.street,
            additionalStreet: values.additionalStreet,
            city: values.city,
            citySubdivision: values.citySubdivision,
            building: values.building,
            postalZone: values.postalZone,
            plotIdentification: values.plotIdentification,
            countrySubEntity: values.countrySubEntity,
            countryCode: values.countryCode || 'SA'
          }

          if (selectedItem.new) {
            insuranceCompany = await addNewInsuranceCompany({ variables })
            if (key === 'view') {
              navigate(`/associates/insurance-company/${insuranceCompany.data.addNewInsuranceCompany.id}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
            } else {
              handleClose(insuranceCompany.data.addNewInsuranceCompany)
            }
          } else {
            insuranceCompany = await editInsuranceCompany({
              variables: {
                ...variables,
                insuranceCompany: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (insuranceCompany.data.addNewInsuranceCompany && insuranceCompany.data.addNewInsuranceCompany.id) ||
                (insuranceCompany.data.editInsuranceCompany && insuranceCompany.data.editInsuranceCompany.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  const handleDelete = async () => {
    try {
      await deleteInsuranceCompany({
        variables: {
          insuranceCompany: selectedItem.id
        }
      })
      message.success(t('settings.insurance.companyDeleted'))
      handleClose()
      navigate('/associates/insurance-companies')
    } catch (e) {
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  return (
    <StyledDrawer
      title={
        selectedItem.new
          ? t('settings.insurance.newInsurance')
          : `${t('settings.insurance.editInsurance')} - ( ${selectedItem.name} )`
      }
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new && user.permissions.deleteInsuranceCompany ? (
            <Popconfirm
              title={t('settings.insurance.sureDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={handleDelete}
              disabled={loading || loadingEdit}
            >
              <Button loading={loadingDelete} disabled={loading || loadingEdit} type="primary" danger icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmitCompany} disabled={loading} />
          ) : (
            <span />
          )}
          <Button
            loading={loading || loadingEdit}
            disabled={loadingDelete}
            htmlType="submit"
            type="primary"
            form={`${generatedID}_InsuranceCompanyForm`}
          >
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingDelete || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}_InsuranceCompanyForm`}
          onFinish={handleSubmitCompany}
          initialValues={{
            ...selectedItem,
            priceList: selectedItem.priceList ? { label: selectedItem.priceList.name, key: selectedItem.priceList.id } : null,
            number: selectedItem.number?.split('_')[1] || '',
            suffix: selectedItem.number?.split('_').length > 1 ? selectedItem.number.split('_')[0] : user.company.code,
            nphiesCode: selectedItem.nphiesCode || '',
            nphiesInsuranceCompanyId: selectedItem.nphiesInsuranceCompanyId || '',
            tpaNphiesCode: selectedItem.tpaNphiesCode || ''
          }}
        >
          <Row type="flex" gutter={16}>
            <Col md={12} xs={24}>
              <Form.Item
                label={t('calendar.companyName')}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t('calendar.insuranceName'),
                    whitespace: true
                  },
                  { max: 100, message: t('calendar.companyNameLong') }
                ]}
              >
                <Input autoComplete="off" autoFocus />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <PhoneField
                getFieldValue={getFieldValue}
                setFieldsValue={setFieldsValue}
                label={t('calendar.companyNumber')}
                fieldName="number"
              />
            </Col>
            <Col md={12} xs={24}>
              <EmailField />
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                label={t('insuranceCompany.taxNumber')}
                name="taxNumber"
                extra={t('insuranceCompany.taxNumberHint')}
                rules={[
                  {
                    pattern: /^[0-9]{15}$/,
                    message: t('insuranceCompany.taxNumberInvalid')
                  }
                ]}
              >
                <Input
                  autoComplete="off"
                  placeholder="123456789012345"
                  maxLength={15}
                  parser={value => toEnglishNumbers(value)}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Address Section for ZATCA B2B Invoices */}
          <Row type="flex" gutter={16}>
            <Col span={24}>
              <h4>{t('insuranceCompany.addressSection')}</h4>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                label={t('insuranceCompany.street')}
                name="street"
                rules={[{ max: 127, message: t('insuranceCompany.streetTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.streetPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                label={t('insuranceCompany.additionalStreet')}
                name="additionalStreet"
                rules={[{ max: 127, message: t('insuranceCompany.streetTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.additionalStreetPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item
                label={t('insuranceCompany.city')}
                name="city"
                rules={[{ max: 127, message: t('insuranceCompany.cityTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.cityPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item
                label={t('insuranceCompany.district')}
                name="citySubdivision"
                rules={[{ max: 127, message: t('insuranceCompany.districtTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.districtPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item
                label={t('insuranceCompany.building')}
                name="building"
                rules={[{ max: 50, message: t('insuranceCompany.buildingTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.buildingPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item
                label={t('insuranceCompany.postalCode')}
                name="postalZone"
                rules={[
                  {
                    pattern: /^[0-9]{5}$/,
                    message: t('insuranceCompany.postalCodeInvalid')
                  }
                ]}
              >
                <Input autoComplete="off" placeholder="12345" maxLength={5} parser={value => toEnglishNumbers(value)} />
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item
                label={t('insuranceCompany.plotIdentification')}
                name="plotIdentification"
                rules={[{ max: 50, message: t('insuranceCompany.plotTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.plotPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={8} xs={24}>
              <Form.Item
                label={t('insuranceCompany.countrySubEntity')}
                name="countrySubEntity"
                rules={[{ max: 50, message: t('insuranceCompany.countrySubEntityTooLong') }]}
              >
                <Input autoComplete="off" placeholder={t('insuranceCompany.countrySubEntityPlaceholder')} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.insuranceStartMonth')} name="insuranceStartMonth" extra={t('app.ifEmpty')}>
                <Select className="fixArrowSelection" showAction={['click', isMobile ? '' : 'focus']} allowClear>
                  {new Array(12).fill(1).map((_, i) => (
                    <Select.Option value={i} key={i}>
                      {moment()
                        .month(i)
                        .format('MMMM')}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('app.forcePolicySelect')} colon={false} name="forcePolicySelect" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
            {hasDhsFeature && (
            <Col md={12} xs={24}>
              <Form.Item
                label={<LabelWithHelp label={t('dhs.nphiesCode') || 'NPHIES Code'} tooltip={t('dhs.nphiesCodeTooltip')} />}
                name="nphiesCode"
              >
                <Input autoComplete="off" placeholder={t('dhs.nphiesCode') || 'NPHIES Code'} />
              </Form.Item>
            </Col>
            )}
            {hasDhsFeature && (
            <Col md={12} xs={24}>
              <Form.Item
                label={<LabelWithHelp label={t('dhs.tpaNphiesCode') || 'TPA NPHIES Code'} tooltip={t('dhs.tpaNphiesCodeTooltip')} />}
                name="tpaNphiesCode"
              >
                <Input autoComplete="off" placeholder={t('dhs.tpaNphiesCode') || 'TPA NPHIES Code'} />
              </Form.Item>
            </Col>
            )}
            {hasDhsFeature && (
            <Col md={12} xs={24}>
              <Form.Item
                label={<LabelWithHelp label={t('dhs.nphiesInsuranceCompanyId') || 'NPHIES Insurance Company ID'} tooltip={t('dhs.nphiesInsuranceCompanyIdTooltip')} />}
                name="nphiesInsuranceCompanyId"
              >
                <Input autoComplete="off" placeholder={t('dhs.nphiesInsuranceCompanyId') || 'NPHIES Insurance Company ID'} />
              </Form.Item>
            </Col>
            )}
            <Col md={12} xs={24}>
              <SearchField
                formName="insuranceCompanyForm"
                GET_QUERY={SEARCH_PRICE_LISTS_QUERY}
                label={t('settings.branches.defaultPriceList')}
                fieldName="priceList"
                dataName="searchPriceLists"
                allowClear
              />
            </Col>
            {forcePolicySelect ? null : (
              <>
                <Col md={12} xs={24}>
                  <Form.Item label={t('app.approvalRequired')} colon={false} name="approvalRequired" valuePropName="checked">
                    <Checkbox />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          <Row type="flex" gutter={16}>
            {forcePolicySelect ? null : (
              <>
                <Col md={12} xs={24}>
                  <Space wrap size="large">
                    <AmountPriceField
                      name="insuranceLimit"
                      label={t('calendar.insuranceLimit')}
                      disabled={insuranceUnlimited}
                      step={100}
                      rules={[{ type: 'number', required: !insuranceUnlimited, message: t('calendar.insuranceLimitErr') }]}
                    />
                    <Form.Item name="insuranceUnlimited" label={t('calendar.unlimited')} valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                  </Space>
                </Col>
                <Col md={12} xs={24}>
                  <Space wrap size="large">
                    <Form.Item
                      label={t('calendar.insuranceDiscount')}
                      name="insurancePercentage"
                      rules={[{ required: !fullDiscount, message: t('app.fieldRequired') }]}
                    >
                      <InputNumber
                        disabled={fullDiscount}
                        min={0}
                        max={100}
                        step={10}
                        addonAfter="%"
                        parser={value => toEnglishNumbers(value)}
                      />
                    </Form.Item>
                    <Form.Item name="fullDiscount" label={'100%'} valuePropName="checked">
                      <Checkbox />
                    </Form.Item>
                  </Space>
                </Col>
                <br />
                <br />
              </>
            )}
            <Col md={12} xs={24}>
              <Form.Item label={`${t('settings.account.tax')} %`} name="taxPercent">
                <InputNumber min={0} max={100} step={1} addonAfter="%" parser={value => toEnglishNumbers(value)} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={t('common.details')} name="details" rules={[{ max: 1000, message: t('calendar.detailsLong') }]}>
                <Input.TextArea dir="auto" rows={4} maxLength={1000} showCount placeholder={t('calendar.addDetails')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="disabled" valuePropName="checked">
                <Checkbox>{t('common.disabled')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

InsuranceCompanyForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default InsuranceCompanyForm
